// mailLinkSnapshot.ts — J3/K1: DER eine Snapshot-Pfad einer Graph-Mail für die
// MailLink-Verknüpfung.
//
// Die Felder sind 1:1 die aus `MailDetailWindow.mailSnapshot()`. Bewusst als
// pure Funktion an einer Stelle: der Termine-Bereich der Mail-Leiste verknüpft
// jetzt ebenfalls Mails, und zwei driftende Snapshot-Varianten wären genau die
// Art Fehler, die man erst Monate später an einem fehlenden Betreff bemerkt.

import type { Email } from "../../services/graphMailService";
import type { MailTerminLinkInput } from "../../services/calendarTerminService";

/** Leerer/whitespace-Text wird null — nie ein leerer String im Snapshot. */
const oderNull = (s: string | null | undefined): string | null => {
    const t = (s ?? "").trim();
    return t.length > 0 ? t : null;
};

/** Ein Datum als ISO-String; ungültig/fehlend ⇒ null. */
const isoOderNull = (d: Date | string | null | undefined): string | null => {
    if (!d) return null;
    const date = d instanceof Date ? d : new Date(d);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

/**
 * „An"-Snapshot aus den To-Empfängern der Graph-Mail („Name <adresse>", mit
 * Komma getrennt — dasselbe Format wie die übrigen Schreibwege). Früher las der
 * Snapshot ein Feld `recipients`, das eine Graph-`Email` gar nicht hat: jede
 * gezogene oder an einen Termin gehängte Mail landete ohne „An" in der
 * Verknüpfung, und der Kommunikations-Reiter zeigte die Spalte leer.
 */
export function empfaengerText(email: Pick<Email, "to">): string | null {
    const teile = (email.to ?? [])
        .map((r) => {
            const name = (r?.name ?? "").trim();
            const adresse = (r?.address ?? "").trim();
            return name && adresse && name.toLowerCase() !== adresse.toLowerCase() ? `${name} <${adresse}>` : name || adresse;
        })
        .filter(Boolean);
    return teile.length ? teile.join(", ") : null;
}

export function mailLinkSnapshot(mailbox: string, email: Email): MailTerminLinkInput {
    return {
        mailbox,
        messageId: email.id,
        conversationId: oderNull(email.conversationId),
        // Ohne sie entdoppelt der Server zwei Kopien DERSELBEN Mail nicht:
        // `messageId` ist die Graph-`id` und je Ordner verschieden.
        internetMessageId: oderNull(email.internetMessageId),
        subject: oderNull(email.subject),
        senderName: oderNull(email.senderName),
        senderEmail: oderNull(email.senderEmail),
        recipients: oderNull((email as { recipients?: string | null }).recipients) ?? empfaengerText(email),
        receivedAt: isoOderNull(email.date),
        preview: oderNull(email.preview),
    };
}
